import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";
import type { ServiceStandardMappingDTO } from "../api/client";

type ServiceStandardMappingRow = ServiceStandardMappingDTO;

type ServiceStandardMappingTableProps = {
  data: ServiceStandardMappingRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (mapping: ServiceStandardMappingRow) => void;
  onDelete: (mapping: ServiceStandardMappingRow) => void;
};

export function ServiceStandardMappingTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDelete,
}: ServiceStandardMappingTableProps) {
  const { has } = usePermissions();

  const columns: TanstackReactTable.ColumnDef<ServiceStandardMappingRow>[] = [
    createSortableColumn<ServiceStandardMappingRow>(
      "serviceStandardName",
      "ມາດຕະຖານບໍລິການ",
      {
        size: 200,
      },
    ),
    createSortableColumn<ServiceStandardMappingRow>("positionTitle", "ຕໍາແໜ່ງ", {
      size: 150,
      cell: ({ row }) => {
        const title = row.original.positionTitle;
        return title ? (
          <Badge variant="outline">{title}</Badge>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        );
      },
    }),
    {
      id: "checklistName",
      header: "Checklist",
      cell: ({ row }) => {
        const name = row.original.checklistName;
        return name ? (
          <Badge variant="outline">{name}</Badge>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        );
      },
      size: 150,
    },
    {
      id: "qcStandardName",
      header: "ມາດຕະຖານ QC",
      cell: ({ row }) => {
        const name = row.original.qcStandardName;
        return name ? (
          <Badge variant="outline">{name}</Badge>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        );
      },
      size: 150,
    },
    {
      id: "kpiName",
      header: "KPI",
      cell: ({ row }) => {
        const name = row.original.kpiName;
        return name ? (
          <Badge variant="outline">{name}</Badge>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        );
      },
      size: 120,
    },
    {
      id: "createdAt",
      header: "ວັນທີສ້າງ",
      cell: ({ row }) => (
        <span className="text-sm">
          {formatDateTimeLocal(row.original.createdAt)}
        </span>
      ),
      size: 120,
    },
    {
      size: 120,
      id: "actions",
      cell: ({ row }: { row: { original: ServiceStandardMappingRow } }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          onClick: () => void;
          variant?: "destructive";
        }[] = [];

        if (has(Permissions.service_standard.update)) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });
        }

        if (has(Permissions.service_standard.update)) {
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບການເຊື່ອມຕໍ່",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບການເຊື່ອມຕໍ່ນີ້?",
                actionText: "ລຶບ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) {
                onDelete(row.original);
              }
            },
          });
        }

        return <RowActions actions={actions} />;
      },
    },
  ];

  return (
    <div data-tourid="mapping-table">
      <DataTable<ServiceStandardMappingRow, unknown>
        data={data}
        columns={columns}
        isLoading={isLoading}
        pagination={{
          offset,
          limit,
          total: totalCount,
          onPaginationChange,
        }}
        sorting={{
          sortBy,
          sortOrder,
          onSortingChange,
        }}
        noDataMessage="ບໍ່ມີການເຊື່ອມຕໍ່"
      />
    </div>
  );
}
